<template>
    <section v-if="renderedPropsLoaded">
        <!--------------------------->
        <!-- COMPLETED APPOINTMENT -->
        <!--------------------------->
        <template v-if="appointmentCompleted">
            <div v-if="appointment.approved_by_user" class="alert alert-success mb-2">
                This appointment was approved by {{ appointment.approved_by_user }}.
            </div>
            <p class="font-italic">This appointment is completed and cannot be edited.</p>
            <div class="w-100 text-center my-3">
                <button class="btn btn-sm btn-primary" @click="backToAppointment()">
                    View appointment
                </button>
            </div>
        </template>

        <template v-if="appointment.status === 'new'">
            <!--------------------------->
            <!----- NEW APPOINTMENT ----->
            <!--------------------------->
            <template v-if="isSupervisor">
                <p class="font-italic">This appointment has not been submitted for approval yet.</p>
            </template>

            <template v-else="">
                <p class="text-justify" data-private="lipsum">
                    I,
                    <span v-if="therapistRequiresSupervision">{{ appointmentTherapist.name }}</span>
                    <span v-else="" class="form-inline d-inline">
                        <select
                            :disabled="appointmentCompleted"
                            v-model="appointment_.billing_therapist_id"
                            class="form-control form-control-sm"
                        >
                            <option selected :value="appointmentTherapist.id">{{
                                appointmentTherapist.name
                            }}</option>
                            <option
                                v-if="currentUserTherapist.id !== appointmentTherapist.id"
                                :value="currentUserTherapist.id"
                                >{{ currentUserTherapist.name }}</option
                            >
                        </select>
                    </span>
                    , {{ patient ? "saw " + patient.name : "completed this appointment " }} on
                    {{ moment(appointment.time_start_at).format("dddd, MMMM Do YYYY") }} from
                    {{ moment(appointment.time_start_at).format("hh:mm A") }} to
                    {{ moment(appointment.time_end_at).format("hh:mm A") }} for
                    {{ appointmentType ? ` for ${appointmentType.name}` : "" }}
                    {{ appointment.address ? ` at ${appointment.address}` : "" }}.
                </p>

                <div class="form-group row mb-2" v-if="appointmentType.is_billable">
                    <label
                        for="data-tracked"
                        class="col-6 col-form-label font-weight-bold text-nowrap"
                        >Data tracked:</label
                    >
                    <select
                        :disabled="appointmentCompleted"
                        id="data-tracked"
                        class="col-6 form-control form-control-sm"
                        v-model="appointment_.is_data_tracked"
                    >
                        <option :value="null">Choose one</option>
                        <option :value="0">No</option>
                        <option :value="1">Yes</option>
                    </select>
                </div>

                <template
                    v-if="
                        ['professional_platinum'].includes(organization.type) &&
                            appointmentType.is_billable
                    "
                >
                    <template
                        v-if="
                            helpers._get(contract, 'funding_source.type', null) ===
                                'insurance_provider'
                        "
                    >
                        <div class="form-group row mb-2">
                            <label
                                for="report-cpt"
                                class="col-6 col-form-label font-weight-bold text-nowrap"
                                >Choose CPT code:</label
                            >
                            <template v-if="patient && contract">
                                <select
                                    :disabled="appointmentCompleted"
                                    class="col-6 form-control form-control-sm"
                                    id="report-cpt"
                                    v-model="appointment_.preferred_cpt_code_id"
                                >
                                    <option v-for="c in contract.cpt_codes" :value="c.id">{{
                                        `${c.code} - ${c.service_nickname}`
                                    }}</option>
                                </select>
                            </template>
                        </div>

                        <div class="form-group row mb-2">
                            <label
                                for="report-diagnoses"
                                class="col-6 col-form-label font-weight-bold text-nowrap"
                                >Choose diagnoses:</label
                            >
                            <div class="col-6 p-0">
                                <template v-if="patient && contract">
                                    <vw-multi-select
                                        id="report-diagnoses"
                                        v-model="appointment_.diagnoses"
                                        :styles="{ input: { width: '100%' } }"
                                        :template="'bootstrap'"
                                        :iconset="'font-awesome'"
                                        :list="contract.diagnoses"
                                        :value-property="'code'"
                                        :searchable="true"
                                        :tags="true"
                                        :selected-list-display-function="item => item.code"
                                        :list-item-display-function="
                                            item => `${item.code} - ${item.service_nickname}`
                                        "
                                    ></vw-multi-select>
                                </template>
                            </div>
                        </div>
                    </template>

                    <template v-else="">
                        <div class="form-group row mb-2" v-if="patient && contract">
                            <label
                                for="report-service"
                                class="col-6 col-form-label font-weight-bold text-nowrap"
                                >Choose Service code:</label
                            >
                            <template>
                                <select
                                    :disabled="appointmentCompleted"
                                    class="col-6 form-control form-control-sm"
                                    id="report-service"
                                    v-model="appointment_.service_codes"
                                >
                                    <option v-for="c in contract.service_codes" :value="[c.code]">{{
                                        `${c.code} - ${c.service_nickname}`
                                    }}</option>
                                </select>
                            </template>
                        </div>
                    </template>
                </template>

                <template v-if="therapistRequiresSupervision">
                    <div class="form-group row mb-2">
                        <label
                            for="billing_therapist_id"
                            class="col-6 col-form-label font-weight-bold text-nowrap"
                            >Appointment supervised by:</label
                        >
                        <template>
                            <select
                                :disabled="appointmentCompleted"
                                class="col-6 form-control form-control-sm"
                                id="billing_therapist_id"
                                v-model="appointment_.billing_therapist_id"
                            >
                                <option selected :value="null">&lt;Not supervised&gt;</option>
                                <option v-for="s in availableSupervisors" :value="s.id">{{
                                    s.name
                                }}</option>
                            </select>
                        </template>
                    </div>
                </template>

                <div class="row">
                    <div class="col-6 pr-1">
                        <p>{{ helpers._get(billingTherapist, "name", "") }}</p>
                        <div>
                            <div
                                class="mt-3"
                                v-show="helpers._get(billingTherapist, 'user.signature', false)"
                            >
                                <label for="therapist-sign-appointment">
                                    <input
                                        type="checkbox"
                                        id="therapist-sign-appointment"
                                        v-model="is_signed"
                                    />
                                    Sign Report
                                </label>
                                <img
                                    id="therapist-signature"
                                    v-if="
                                        is_signed &&
                                            helpers._get(billingTherapist, 'user.signature', false)
                                    "
                                    class="signatureWrapper"
                                    :src="helpers._get(billingTherapist, 'user.signature', null)"
                                    alt=""
                                />
                            </div>
                            <div
                                v-show="!helpers._get(billingTherapist, 'user.signature', false)"
                                class="mt-3"
                            >
                                <p
                                    v-if="
                                        helpers._get(billingTherapist, 'id', null) ===
                                            helpers._get(currentUserTherapist, 'id', 0)
                                    "
                                >
                                    No signature found. Please first
                                    <a href="/account-settings">upload a signature</a> before
                                    signing.
                                </p>
                                <p v-else="">
                                    No signature found.
                                    {{ helpers._get(billingTherapist, "name", "The therapist") }}
                                    needs to add a signature in the Account Settings page before
                                    signing.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="row mt-3" v-show="appointmentType.is_billable">
                    <div class="col-md-8">
                        <label for="caregiverSignSession" class="mb-1">
                            <input
                                id="caregiverSignSession"
                                type="checkbox"
                                v-model="caregiverSignSession"
                            />
                            Caregiver Signature
                        </label>
                        <div v-show="caregiverSignSession">
                            <a
                                @click="clearSignature"
                                class="font-weight-bold link-primary float-right text-primary cursor-pointer"
                                >Clear</a
                            >
                            <canvas id="session-signature" class="signatureWrapper"></canvas>
                            <i>*Please sign in the box above</i>
                        </div>
                    </div>
                </div>

                <div class="my-2 w-100 d-flex justify-content-end">
                    <button
                        class="btn btn-sm btn-primary text-white text-uppercase font-weight-bold"
                        @click="saveReport()"
                    >
                        Save Report
                    </button>
                </div>
            </template>
        </template>

        <template v-if="appointment.status === 'pending_supervisor'">
            <!--------------------------->
            <!--- APPROVE APPOINTMENT --->
            <!--------------------------->
            <template v-if="appointmentTherapist.id === currentUserTherapist.id">
                <p class="font-italic">
                    This appointment has already been submitted and is awaiting approval.
                </p>
            </template>

            <template v-else="">
                <p class="text-justify" data-private="lipsum">
                    I, {{ appointmentTherapist.name }}, saw
                    {{ patient && patient.name ? patient.name : "the patient" }} on
                    {{ moment(appointment.time_start_at).format("dddd, MMMM Do YYYY") }} from
                    {{ moment(appointment.time_start_at).format("hh:mm A") }} to
                    {{ moment(appointment.time_end_at).format("hh:mm A") }} for
                    {{ appointmentType ? appointmentType.name : "Appointment" }} at
                    {{ organization.location || "location" }}.
                </p>

                <template
                    v-if="
                        ['professional_platinum'].includes(organization.type) &&
                            appointmentType.is_billable
                    "
                >
                    <template
                        v-if="
                            helpers._get(contract, 'funding_source.type', null) ===
                                'insurance_provider'
                        "
                    >
                        <div class="form-group row mb-2">
                            <label
                                for="report-cpt-approve"
                                class="col-6 col-form-label font-weight-bold text-nowrap"
                                >Choose CPT code:</label
                            >
                            <template v-if="patient && contract">
                                <select
                                    :disabled="appointmentCompleted"
                                    class="col-6 form-control form-control-sm"
                                    id="report-cpt-approve"
                                    v-model="appointment_.preferred_cpt_code_id"
                                >
                                    <option v-for="c in contract.cpt_codes" :value="c.id">{{
                                        `${c.code} - ${c.service_nickname}`
                                    }}</option>
                                </select>
                            </template>
                        </div>

                        <div class="form-group row mb-2">
                            <label
                                for="report-diagnoses-approve"
                                class="col-6 col-form-label font-weight-bold text-nowrap"
                                >Choose diagnoses:</label
                            >
                            <template v-if="patient && contract">
                                <div class="col-6 px-0">
                                    <vw-multi-select
                                        id="report-diagnoses-approve"
                                        v-model="appointment_.diagnoses"
                                        :disabled="appointmentCompleted"
                                        :template="'bootstrap'"
                                        :iconset="'font-awesome'"
                                        :styles="{ input: { width: '100%' } }"
                                        :list="contract.diagnoses"
                                        :value-property="'code'"
                                        :searchable="true"
                                        :tags="true"
                                        :selected-list-display-function="item => item.code"
                                        :list-item-display-function="
                                            item => `${item.code} - ${item.service_nickname}`
                                        "
                                    ></vw-multi-select>
                                </div>
                            </template>
                        </div>
                    </template>

                    <template v-else="">
                        <div class="form-group row mb-2">
                            <label
                                for="report-service-approve"
                                class="col-6 col-form-label font-weight-bold text-nowrap"
                                >Choose Service code:</label
                            >
                            <template v-if="patient && contract">
                                <select
                                    :disabled="appointmentCompleted"
                                    class="col-6 form-control form-control-sm"
                                    id="report-service-approve"
                                    v-model="appointment_.service_codes"
                                >
                                    <option v-for="c in contract.service_codes" :value="[c.code]">{{
                                        `${c.code} - ${c.service_nickname}`
                                    }}</option>
                                </select>
                            </template>
                        </div>
                    </template>
                </template>

                <div class="row mb-4">
                    <div class="col-6 pr-1">
                        <p>{{ appointmentTherapist.name }}</p>
                        <div>&nbsp;</div>
                        <div class="signature">
                            <img
                                v-if="
                                    ['complete', 'pending_supervisor'].includes(appointment.status)
                                "
                                class="container-fluid"
                                :src="helpers._get(appointmentTherapist, 'user.signature', null)"
                                alt=""
                            />
                        </div>
                    </div>

                    <div class="col-6 pl-1">
                        <p class="mb-0">{{ currentUserTherapist.name }} - Supervisor</p>
                        <div>
                            <label for="supervisor-sign-appointment">
                                <input
                                    type="checkbox"
                                    id="supervisor-sign-appointment"
                                    v-model="is_signed"
                                />
                                Sign Report
                            </label>
                        </div>
                        <div
                            v-show="
                                ['pending_supervisor'].includes(appointment.status) &&
                                    currentUserTherapist &&
                                    currentUserTherapist.id === appointment_.billing_therapist_id
                            "
                        >
                            <div class="signature" v-show="user.signature">
                                <img
                                    v-if="is_signed && user.signature"
                                    class="container-fluid"
                                    :src="user.signature"
                                    alt=""
                                />
                            </div>
                            <div v-show="!user.signature" class="mt-3">
                                <p>
                                    No signature found. Please first
                                    <a href="/account-settings">upload a signature</a> before
                                    signing.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="row mt-3" v-show="appointmentType.is_billable">
                    <div class="col-md-8">
                        <label for="caregiverSignSession" class="mb-1">
                            <input
                                id="caregiverSignSession"
                                type="checkbox"
                                v-model="caregiverSignSession"
                            />
                            Caregiver Signature
                        </label>
                        <div v-show="caregiverSignSession">
                            <a
                                @click="clearSignature"
                                class="font-weight-bold link-primary float-right text-primary cursor-pointer"
                                >Clear</a
                            >
                            <canvas id="session-signature" class="signatureWrapper"></canvas>
                            <i>*Please sign in the box above</i>
                        </div>
                    </div>
                </div>

                <div class="my-2 w-100 d-flex justify-content-end">
                    <button
                        class="btn btn-sm btn-primary text-white text-uppercase font-weight-bold"
                        @click="saveReport()"
                    >
                        Approve Report
                    </button>
                </div>
            </template>
        </template>

        <div v-show="validationMessage" class="alert alert-danger">
            {{ validationMessage }}
        </div>

        <errors :errors="errors"></errors>
    </section>
    <section v-else="">
        <!------------------------------------------->
        <!-- USERS THAT AREN'T THERAPISTS SEE THIS -->
        <!------------------------------------------->
        <template v-if="appointment.status === 'new'">
            <p class="font-italic">This appointment has not been submitted for approval yet.</p>
        </template>
        <template v-if="appointment.status === 'pending_supervisor'">
            <p class="font-italic">
                This appointment has already been submitted and is awaiting approval.
            </p>
        </template>
        <template v-if="appointmentCompleted">
            <p class="font-italic">This appointment is completed and cannot be edited.</p>
        </template>
    </section>
</template>

<script>
import { helpers } from "../../plugins/helpers";
import { errors } from "../../plugins/errors";
import SignaturePad from "signature_pad";

export default {
    name: "SessionConfirmation",
    props: [
        "user",
        "currentUserTherapist",
        "organization",
        "appointment",
        "appointmentType",
        "scheduleOwner",
        "scheduleOwnerProp",
        "participant",
        "contract",
        "organization",
    ],
    mixins: [errors],
    data() {
        return {
            helpers,
            supervisors: [],
            therapists: [],
            is_signed: false,
            appointment_: {
                charge_amount: null,
                approved_by_user_id: null,
                diagnoses: [],
                cpt_codes: null,
                preferred_cpt_code_id: null,
                service_codes: null,
                contract_id: null,
                is_private_pay: null,
                billing_therapist_id: null,
                is_data_tracked: null,
                therapist: {},
            },
            validationMessage: "",
            signaturePad: null,
            signatureError: null,
            caregiverSignSession: false,
        };
    },
    created() {
        this.fetchSupervisors();
        const canvas = document.querySelector(`#session-signature`);
    },
    mounted() {
        const canvas = document.querySelector(`#session-signature`);
        if (canvas) {
            this.signaturePad = new SignaturePad(canvas);
        }
    },

    computed: {
        patient() {
            switch (this.scheduleOwnerProp) {
                case "patient":
                    return this.scheduleOwner;
                case "therapist":
                    return this.participant;
            }
        },

        appointmentTherapist() {
            return this.appointment_.therapist;
        },

        billingTherapist() {
            return this.therapists.find(
                therapist => therapist.id === _.get(this.appointment_, "billing_therapist_id", null)
            );
        },

        availableSupervisors() {
            return this.supervisors.filter(s => s.id !== this.appointmentTherapist.id);
        },

        isSupervisor() {
            if (this.appointment && this.appointment.status === "new") {
                return false;
            }
            return this.appointment.therapist_id !== this.appointment.therapist_id;
        },

        therapistRequiresSupervision() {
            return this.appointmentTherapist.needs_supervision;
        },

        appointmentCompleted() {
            return this.appointment.status === "complete";
        },

        renderedPropsLoaded() {
            return !!(this.currentUserTherapist && this.currentUserTherapist.id);
        },
    },

    methods: {
        populateAppointment() {
            Object.keys(this.appointment_).forEach(k => {
                this.appointment_[k] = this.appointment[k];
            });
            if (!this.appointment.diagnoses) {
                this.appointment_.diagnoses = [];
            }
            this.appointment_.therapist = this.therapists.find(
                t => t.id === this.appointment.therapist_id
            );
            if (!_.get(this.appointment_, "billing_therapist_id", false)) {
                console.log(this.appointment.therapist_id);
                this.appointment_.billing_therapist_id = this.appointment.therapist_id;
            }

            if (
                !this.appointment.preferred_cpt_code_id &&
                (this.appointment.cpt_codes && this.appointment.cpt_codes.length)
            ) {
                let code = this.contract.cpt_codes.find(function(code) {
                    return code.code == this.appointment.cpt_codes[0];
                });

                if (code) {
                    this.appointment_.preferred_cpt_code_id = code.id;
                }
            }
        },

        fetchSupervisors() {
            //Therapists
            axios
                .get("/api/organizations/" + this.organization.id + "/therapists")
                .then(r => {
                    this.therapists = r.data.data;
                    this.therapists.forEach(t => (t.name = t.user.name));
                    this.supervisors = [
                        ...this.therapists.filter(t => t.id !== this.appointmentTherapist.id),
                    ];
                    this.populateAppointment();
                })
                .catch(error => {
                    console.error(error);
                });
        },

        validateReport() {
            this.validationMessage = "";

            if (!this.appointment_.is_data_tracked && this.appointmentType.is_billable) {
                this.validationMessage =
                    "Data tracked must be set to 'yes' for billable appointments.";
                return false;
            }

            if (!this.is_signed) {
                this.validationMessage = "Report must be signed in order to be approved.";
                return false;
            }

            if (this.appointment.status === "new") {
                if (this.appointment.is_private_pay) {
                    return true;
                }
                if (["professional_platinum"].includes(this.organization.type)) {
                    if (this.appointmentType.is_billable) {
                        if (
                            _.get(this.contract, "funding_source.type", null) ===
                            "insurance_provider"
                        ) {
                            if (!this.appointment_.diagnoses.length) {
                                this.validationMessage =
                                    "At least one Diagnosis code needs to be selected.";
                                return false;
                            }
                            if (!this.appointment_.preferred_cpt_code_id) {
                                this.validationMessage = "CPT code needs to be selected.";
                                return false;
                            }
                        } else {
                            if (
                                !this.appointment_.service_codes &&
                                !this.appointment_.service_codes.length
                            ) {
                                this.validationMessage = "Service code needs to be selected.";
                                return false;
                            }
                        }
                    }
                }
            }

            return true;
        },

        preparePayload() {
            let payload = { ...this.appointment_ };

            if (this.appointment.status === "new") {
                if (this.therapistRequiresSupervision && this.appointment_.billing_therapist_id) {
                    //Therapist submits for supervisor review
                    payload.billing_therapist_id = this.appointment_.billing_therapist_id;
                    payload.status = "pending_supervisor";
                } else {
                    //Own therapist approves
                    if (!payload.billing_therapist_id) {
                        payload.billing_therapist_id = this.appointmentTherapist.id;
                    }
                    payload.approved_by_user_id = this.user.id;
                    payload.status = "complete";
                }
            }

            if (payload.contract_id === "private_pay") {
                delete payload.contract_id;
                payload.is_private_pay = 1;
            }

            if (this.appointment.status === "pending_supervisor") {
                payload.billing_therapist_id = this.currentUserTherapist.id;
                payload.status = "complete";
                payload.approved_by_user_id = this.user.id;
            }

            if (!this.signaturePad.isEmpty()) {
                let url = this.signaturePad.toDataURL();
                payload.caregiver_signature = url;
            }

            if (this.is_signed && this.user.signature) {
                payload.therapist_signature = this.user.signature;
            }

            payload = helpers.prepareCodesPayload(payload);

            return payload;
        },

        saveReport() {
            if (!this.validateReport()) return;
            this.setErrors();
            let payload = this.preparePayload();
            payload = helpers.cleanEmptyProperties(payload);
            axios
                .patch(
                    `/api/organizations/${this.organization.id}/scheduling/appointments/${
                        this.appointment.id
                    }`,
                    payload
                )
                .then(r => {
                    this.$emit("close-confirmation");
                })
                .catch(e => {
                    console.error(e);
                    const errors = _.get(e, "response.data.errors", {});
                    const message = _.get(e, "response.data.message", {});
                    if (
                        typeof errors === "object" &&
                        Object.keys(errors) &&
                        Object.keys(errors).length
                    ) {
                        this.setErrors({ ...errors });
                    } else if (typeof message === "string") {
                        this.setErrors([{ message }]);
                    } else if (isArray(message)) {
                        this.setErrors([...message]);
                    }
                });
        },

        clearSignature() {
            this.signaturePad.clear();
        },

        backToAppointment() {
            this.$parent.$parent.showCompleteAppointment = false;
        },
    },
};
</script>

<style scoped lang="scss">
.signature {
    max-height: 4rem;
    width: 100%;
}
.signatureWrapper {
    width: 100%;
    border: 1px solid lightgray;
}
.link-danger {
    color: red;
}
</style>
